import { ComboboxSelect, Stack, Tooltip, Typography } from '@chronosphereio/chrono-ui';
import { displayMetricType } from '@chronosphereio/editors/prometheus';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { useMemo } from 'react';
import { Group } from '@/dashboards/components/Builder';
import { useBuilderContext } from '../promql-builder-context';
import {
  DEFAULT_CUMULATIVE_RANGE,
  DEFAULT_DELTA_RANGE,
  isCumulativeType,
  isDeltaType,
  RANGE_FUNCTION_TO_DEFAULT_INTERVAL,
  RangeFunction,
} from '../promql-builder-model';

export function RangeSelection() {
  const { hasRange, onRangeChange } = useBuilderContext();

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Group.Root onRemove={hasRange ? () => onRangeChange(undefined) : undefined}>
        <Group.Label>Rollup</Group.Label>
        <FunctionSelector />
        {hasRange && (
          <>
            <Group.Label isMuted>over</Group.Label>
            <IntervalSelector />
          </>
        )}
      </Group.Root>
      <Information />
    </Stack>
  );
}

function useDefaultRange() {
  const { metricType } = useBuilderContext();
  if (isDeltaType(metricType)) {
    return DEFAULT_DELTA_RANGE;
  } else {
    return DEFAULT_CUMULATIVE_RANGE;
  }
}

// TODO: Use for range function flyout.
// [
//   {
//     key: 'rate',
//     description: (
//       <>
//         <Typography variant="body2">Increase per-second for cumulative counter</Typography>
//         {/* <Link
//           href="https://prometheus.io/docs/prometheus/latest/querying/functions/#rate"
//           target="_blank"
//           sx={{ display: 'flex', alignItems: 'center' }}
//         >
//           Docs <LaunchIcon fontSize="inherit" sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
//         </Link> */}
//       </>
//     ),
//   },
//   {
//     key: 'increase',
//     description: (
//       <>
//         <Typography variant="body2">Increase for cumulative counter</Typography>
//         {/* <Link
//           href="https://prometheus.io/docs/prometheus/latest/querying/functions/#increase"
//           target="_blank"
//           sx={{ display: 'flex', alignItems: 'center' }}
//         >
//           Docs <LaunchIcon fontSize="inherit" sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
//         </Link> */}
//       </>
//     ),
//   },
//   {
//     key: 'sum_per_second',
//     description: (
//       <>
//         <Typography variant="body2">Increase per-second for delta counter</Typography>
//         {/* <Link
//           href="https://docs.chronosphere.io/investigate/querying/metrics/delta-queries#functions-for-delta-temporality-use-cases"
//           target="_blank"
//           sx={{ display: 'flex', alignItems: 'center' }}
//         >
//           Docs <LaunchIcon fontSize="inherit" sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
//         </Link> */}
//       </>
//     ),
//   },
//   {
//     key: 'sum_over_time',
//     description: (
//       <>
//         <Typography variant="body2">Increase for delta counter</Typography>
//         {/* <Link
//           href="https://docs.chronosphere.io/investigate/querying/metrics/delta-queries#functions-for-delta-temporality-use-cases"
//           target="_blank"
//           sx={{ display: 'flex', alignItems: 'center' }}
//         >
//           Docs <LaunchIcon fontSize="inherit" sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
//         </Link> */}
//       </>
//     ),
//   },
// ];

const CUMULATIVE_TYPE_RANGE_FUNCTIONS = ['rate', 'increase'];
const DELTA_TYPE_RANGE_FUNCTIONS = ['sum_per_second', 'sum_over_time'];

function FunctionSelector() {
  const { range, onRangeChange, hasRange, metricType } = useBuilderContext();

  const functionName = range?.functionName ?? null;

  const options = useMemo(() => {
    if (isCumulativeType(metricType)) {
      return CUMULATIVE_TYPE_RANGE_FUNCTIONS;
    } else if (isDeltaType(metricType)) {
      return DELTA_TYPE_RANGE_FUNCTIONS;
    } else {
      return [...CUMULATIVE_TYPE_RANGE_FUNCTIONS, ...DELTA_TYPE_RANGE_FUNCTIONS];
    }
  }, [metricType]);

  return (
    <ComboboxSelect
      onReset={() => null}
      value={functionName}
      renderValue={
        <Group.Button isLast={!hasRange} isPlaceholder={!hasRange}>
          {functionName ?? 'No function'}
        </Group.Button>
      }
      searchPlaceholder="Search functions"
      allowCustomValue={false}
      options={options}
      onSelect={(option) => {
        if (!option) {
          onRangeChange(undefined);
        } else {
          const rangeFunction = option as RangeFunction;
          onRangeChange({
            functionName: rangeFunction,
            interval: range ? range.interval : RANGE_FUNCTION_TO_DEFAULT_INTERVAL[rangeFunction],
          });
        }
      }}
    />
  );
}

const INTERVAL_OPTIONS = ['$__interval', '$__rate_interval', '1m', '5m', '10m'];

function IntervalSelector() {
  const { range, onRangeChange } = useBuilderContext();
  const defaultRange = useDefaultRange();

  const interval = range?.interval ?? null;
  const hasInterval = !!interval;

  return (
    <ComboboxSelect
      onReset={() => null}
      value={interval}
      renderValue={<Group.Button isPlaceholder={!hasInterval}>{interval ?? '(none)'}</Group.Button>}
      searchPlaceholder="Search or enter a custom value (ex. 2m)"
      allowCustomValue
      options={INTERVAL_OPTIONS}
      onSelect={(option) => {
        if (!option) {
          onRangeChange(undefined);
        } else {
          if (range) {
            onRangeChange({ ...range, interval: option });
          } else {
            onRangeChange({ functionName: defaultRange.functionName, interval: option });
          }
        }
      }}
    />
  );
}

function Information() {
  const { hasMetric, hasMetricType, metric, metricType } = useBuilderContext();

  const content = (
    <>
      <Typography variant="body2">
        Use rate(...) or increase(...) with a cumulative counter or cumulative exponential histogram.
      </Typography>
      <Typography variant="body2">
        Use sum_per_second(...) or sum_over_time(...) with a delta counter or delta exponential histogram.
      </Typography>
      {hasMetric && hasMetricType && (isCumulativeType(metricType) || isDeltaType(metricType)) && (
        <Typography variant="body2">
          {metric} is a {displayMetricType(metricType).toLowerCase()}.
        </Typography>
      )}
      {hasMetric && hasMetricType && !isCumulativeType(metricType) && !isDeltaType(metricType) && (
        <Typography variant="body2">
          {metric} is none of these, so {`it's`} recommended not to use these functions.
        </Typography>
      )}
      {hasMetric && !hasMetricType && <Typography variant="body2">The type of {metric} is not known.</Typography>}
    </>
  );

  return (
    <Tooltip Content={content}>
      <InfoIcon fontSize="inherit" color="secondary" sx={{ alignSelf: 'center', marginLeft: '4px' }} />
    </Tooltip>
  );
}
